import { useState, useEffect } from "react";
import axios from 'axios';
import AppURL from "../components/AppUrl";

interface UserData {
    name: string;
    email: string;
    id: string;
    role: string;
}


const GestionRendezVous = () => {
    const [rendezVous, setRendezVous] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [filter, setFilter] = useState("all"); // 'all', 'confirmed', 'pending'
    const [editingId, setEditingId] = useState<string | null>(null);
    const [currentUser, setCurrentUser] = useState<UserData | null>(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [editForm, setEditForm] = useState({
        date: "",
        heure: "",
        confirmation: Boolean,
    });
    const [confirmingId, setConfirmingId] = useState<string | null>(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [newRendezVous, setNewRendezVous] = useState({
        clientFullName: "",
        clientPhoneNumber: "",
        clientEmail: "",
        date: "",
        heure: "",
        type: "Mécanique",
        description: "",
        preferredLanguage: "fr",
        contactMethod: "phone",
        confirmation: true,
        confirmedBy: currentUser?.name,

    });

    const [dateFilter, setDateFilter] = useState<string>("today"); // 'all', 'today', 'week', 'month', 'custom'
    const [customStartDate, setCustomStartDate] = useState<string>("");
    const [customEndDate, setCustomEndDate] = useState<string>("");


    const filteredRendezVous = rendezVous.filter(rdv => {
        // Filtre par statut
        if (filter === "confirmed" && !rdv.confirmation) return false;
        if (filter === "pending" && rdv.confirmation) return false;

        // Filtre par recherche si searchTerm n'est pas vide
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            if (!(
                rdv.clientFullName.toLowerCase().includes(term) ||
                rdv.clientPhoneNumber.includes(term) ||
                (rdv.clientEmail && rdv.clientEmail.toLowerCase().includes(term))
            )) {
                return false;
            }
        }

        // Filtre par date
        if (dateFilter !== "all") {
            // Convertit rdv.date en objet Date, puis extrait uniquement la partie date (YYYY-MM-DD)
            const rdvDateStr = new Date(rdv.date).toISOString().split('T')[0];

            // Date du jour (locale), convertie aussi en YYYY-MM-DD
            const todayStr = new Date().toISOString().split('T')[0];

            switch (dateFilter) {
                case "today":
                    if (rdvDateStr !== todayStr) return false;
                    break;

                case "week":
                    {
                        const today = new Date(todayStr);
                        const rdvDate = new Date(rdvDateStr);

                        const startOfWeek = new Date(today);
                        startOfWeek.setDate(today.getDate() - today.getDay());

                        const endOfWeek = new Date(startOfWeek);
                        endOfWeek.setDate(startOfWeek.getDate() + 6);

                        // Ignore les heures dans les comparaisons
                        startOfWeek.setHours(0, 0, 0, 0);
                        endOfWeek.setHours(23, 59, 59, 999);

                        if (rdvDate < startOfWeek || rdvDate > endOfWeek) return false;
                    }
                    break;

                case "month":
                    {
                        const today = new Date(todayStr);
                        const rdvDate = new Date(rdvDateStr);

                        if (
                            rdvDate.getMonth() !== today.getMonth() ||
                            rdvDate.getFullYear() !== today.getFullYear()
                        ) return false;
                    }
                    break;

                case "custom":
                    if (!customStartDate || !customEndDate) return false;

                    const rdvDate = new Date(rdvDateStr);
                    const startDate = new Date(customStartDate);
                    const endDate = new Date(customEndDate);

                    // Ignore l'heure
                    startDate.setHours(0, 0, 0, 0);
                    endDate.setHours(23, 59, 59, 999);

                    if (rdvDate < startDate || rdvDate > endDate) return false;
                    break;
            }
        }

        return true;
    });




    // Charger les rendez-vous au montage du composant
    useEffect(() => {
        const userData = localStorage.getItem('userData');
        if (userData) {
            const parsedUser = JSON.parse(userData);
            setCurrentUser(parsedUser);
        }

        // Appel initial sans filtres
        fetchRendezVous();
    }, []);


    const handleCreateChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setNewRendezVous(prev => ({ ...prev, [name]: value }));
    };

    const handleCreateRendezVous = async () => {
        try {
            // Validation des champs requis
            if (!newRendezVous.clientFullName || !newRendezVous.clientPhoneNumber || !newRendezVous.date || !newRendezVous.heure) {
                showNotification("Veuillez remplir tous les champs obligatoires", "error");
                return;
            }

            // Préparation des données avec valeurs par défaut
            const rendezVousData = {
                ...newRendezVous,
                confirmedBy: currentUser?.name || "Administrateur",
                createdAt: new Date().toISOString() // Optionnel: ajout d'un timestamp
            };

            const response = await axios.post(`${AppURL}/api/rendezvous/create-confirm`, rendezVousData);

            if (response.status === 201) {
                showNotification("Rendez-vous créé avec succès", "success");
                setIsCreateModalOpen(false);
                setNewRendezVous({
                    clientFullName: "",
                    clientPhoneNumber: "",
                    clientEmail: "",
                    date: "",
                    heure: "",
                    type: "Mécanique",
                    description: "",
                    preferredLanguage: "français",
                    contactMethod: "phone",
                    confirmation: true,
                    confirmedBy: currentUser?.name ?? "",
                });
                fetchRendezVous();
            }
        } catch (error: any) {
            console.error("Erreur lors de la création:", error);
            showNotification(error.response?.data?.message || "Erreur lors de la création du rendez-vous", "error");
        }
    };


    const tooltipStyles = `
.tooltip {
  position: relative;
  display: inline-block;
}

.tooltip .tooltiptext {
  visibility: hidden;
  width: 300px;
  max-width: min(90vw, 400px);
  background-color: #333;
  color: #fff;
  text-align: left;
  border-radius: 6px;
  padding: 12px;
  position: absolute;
  z-index: 1000;
  opacity: 0;
  transition: opacity 0.2s ease, visibility 0.2s ease;
  white-space: normal;
  word-wrap: break-word;
  max-height: 400px;
  overflow-y: auto;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  
  /* Positionnement décalé vers la droite */
  left: calc(100% + 20px); /* Ajout de 20px de décalage */
  transform: translateX(-50%);
  top: calc(70% + 8px);
  
  /* Vérification du débordement */
  @media (max-height: 600px) {
    max-height: 100px;
  }
}

/* Position alternative si le tooltip sort de l'écran en bas */
.tooltip .tooltiptext.bottom {
  top: auto;
  bottom: calc(100% + 8px);
}

.tooltip:hover .tooltiptext {
  visibility: visible;
  opacity: 1;
}

/* Flèche du tooltip décalée également */
.tooltip .tooltiptext::after {
  content: "";
  position: absolute;
  left: calc(50% - 20px); /* Compensation du décalage */
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: transparent transparent #333 transparent;
  top: -10px;
}

.tooltip .tooltiptext.bottom::after {
  top: auto;
  bottom: -10px;
  border-color: #333 transparent transparent transparent;
  left: calc(50% - 20px); /* Compensation du décalage */
}
`;



    const fetchRendezVous = async (period: string = "today", startDate?: string, endDate?: string) => {
        setLoading(true);
        setError("");


        try {
            // Construction des paramètres de requête
            const params = new URLSearchParams();

            // Gestion des différentes périodes
            if (period !== "all") {
                let startDateFilter: string;
                let endDateFilter: string;

                if (period === "custom") {
                    if (!startDate || !endDate) {
                        throw new Error("Les dates de début et fin sont requises pour une période personnalisée");
                    }
                    startDateFilter = startDate;
                    endDateFilter = endDate;
                } else {
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);

                    switch (period) {
                        case "today":
                            startDateFilter = endDateFilter = today.toISOString().split('T')[0];

                            break;
                        case "week":
                            const startOfWeek = new Date(today);
                            startOfWeek.setDate(today.getDate() - today.getDay());
                            const endOfWeek = new Date(startOfWeek);
                            endOfWeek.setDate(startOfWeek.getDate() + 6);
                            startDateFilter = startOfWeek.toISOString().split('T')[0];
                            endDateFilter = endOfWeek.toISOString().split('T')[0];
                            break;
                        case "month":
                            const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
                            const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
                            startDateFilter = startOfMonth.toISOString().split('T')[0];
                            endDateFilter = endOfMonth.toISOString().split('T')[0];
                            break;
                        default:
                            throw new Error(`Période non reconnue: ${period}`);
                    }
                }

                params.append('startDate', startDateFilter);
                params.append('endDate', endDateFilter);
            }

            // Ajout du paramètre de confirmation si un filtre est appliqué
            if (filter === "confirmed" || filter === "pending") {
                params.append('confirmation', filter === "confirmed" ? 'true' : 'false');
            }

            const url = `${AppURL}/api/rendezvous/getAll?${params.toString()}`;
            const response = await axios.get(url);

            // Tri des rendez-vous par date et heure
            const sortedRendezVous = response.data.sort((a: any, b: any) => {
                try {
                    const dateA = new Date(`${a.date.split('T')[0]}T${a.heure}`);
                    const dateB = new Date(`${b.date.split('T')[0]}T${b.heure}`);
                    return dateA.getTime() - dateB.getTime();
                } catch (error) {
                    console.error("Erreur lors du tri des rendez-vous:", error);
                    return 0;
                }
            });

            setRendezVous(sortedRendezVous);
        } catch (error: any) {
            console.error("Erreur lors de la récupération des rendez-vous:", error);
            setError(error.response?.data?.message || "Erreur lors du chargement des rendez-vous");
        } finally {
            setLoading(false);
        }
    };


    const confirmRendezVous = async (id: string) => {
        try {
            await axios.put(`${AppURL}/api/rendezvous/confirm/${id}`, {
                confirmedBy: currentUser?.name
            });
            showNotification("Rendez-vous confirmé avec succès", "success");
            fetchRendezVous();
            setConfirmingId(null);
        } catch (error) {
            console.error("Erreur lors de la confirmation:", error);
            showNotification("Erreur lors de la confirmation", "error");
        }
    };

    const deleteRendezVous = async (id: string) => {
        const confirmed = window.confirm("Êtes-vous sûr de vouloir supprimer définitivement ce rendez-vous ?");
        if (!confirmed) return;

        try {
            await axios.delete(`${AppURL}/api/rendezvous/${id}`);
            showNotification("Rendez-vous supprimé avec succès", "success");
            fetchRendezVous();
        } catch (error) {
            console.error("Erreur lors de la suppression:", error);
            showNotification("Erreur lors de la suppression", "error");
        }
    };


    const startEditing = (rdv: any) => {
        setEditingId(rdv._id);
        setEditForm({
            date: rdv.date.split('T')[0], // Format pour input date
            heure: rdv.heure,
            confirmation: rdv.confirmation
        });
    };

    const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setEditForm(prev => ({ ...prev, [name]: value }));
    };

    const saveEdit = async () => {
        if (!editingId) return;

        const confirmed = window.confirm("Voulez-vous vraiment modifier ce rendez-vous ?");
        if (!confirmed) return;

        try {
            await axios.put(`${AppURL}/api/rendezvous/${editingId}`, editForm);
            setEditingId(null);
            showNotification("Rendez-vous modifié avec succès", "success");
            fetchRendezVous();
        } catch (error) {
            console.error("Erreur lors de la mise à jour:", error);
            showNotification("Erreur lors de la modification", "error");
        }
    };


    const showNotification = (message: string, type: "success" | "error") => {
        const notification = document.createElement('div');
        notification.className = `
            fixed top-4 right-4 z-50
            bg-white border-l-4 ${type === "success" ? "border-green-500" : "border-red-500"}
            shadow-lg rounded-lg overflow-hidden
            w-full max-w-xs
            transform transition-all duration-300
            opacity-0 translate-y-2
        `;

        notification.innerHTML = `
            <div class="flex items-start p-4">
                <div class="flex-shrink-0">
                    <svg class="h-6 w-6 ${type === "success" ? "text-green-500" : "text-red-500"}" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        ${type === "success" ?
                '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />' :
                '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />'}
                    </svg>
                </div>
                <div class="ml-3 w-0 flex-1 pt-0.5">
                    <p class="text-sm font-medium text-gray-900">
                        ${type === "success" ? "Succès!" : "Erreur!"}
                    </p>
                    <p class="mt-1 text-sm text-gray-500">
                        ${message}
                    </p>
                </div>
                <div class="ml-4 flex-shrink-0 flex">
                    <button class="inline-flex text-gray-400 hover:text-gray-500 focus:outline-none">
                        <span class="sr-only">Fermer</span>
                        <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                        </svg>
                    </button>
                </div>
            </div>
        `;

        document.body.appendChild(notification);

        setTimeout(() => {
            notification.classList.remove('opacity-0', 'translate-y-2');
            notification.classList.add('opacity-100', 'translate-y-0');
        }, 10);

        const closeBtn = notification.querySelector('button');
        closeBtn?.addEventListener('click', () => {
            notification.classList.add('opacity-0', 'translate-y-2');
            setTimeout(() => notification.remove(), 300);
        });

        setTimeout(() => {
            notification.classList.add('opacity-0', 'translate-y-2');
            setTimeout(() => notification.remove(), 300);
        }, 5000);
    };



    const formatDate = (dateString: string) => {
        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            weekday: 'long',
            timeZone: 'UTC' // Ajout de cette ligne pour forcer l'utilisation d'UTC
        };

        // S'assurer que la date est bien interprétée comme UTC
        const date = new Date(dateString);
        const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);

        return utcDate.toLocaleDateString('fr-FR', options);
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                <span className="ml-3">Chargement des rendez-vous...</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
                <p>{error}</p>
                <button
                    onClick={() => fetchRendezVous()}
                    className="mt-2 px-3 py-1 bg-red-600 text-white rounded hover:bg-red-700"
                >
                    Réessayer
                </button>
            </div>
        );
    }

    return (

        <><div className="px-4 py-8 mt-15">
            <div className="bg-white ">
                <h1 className="text-3xl font-bold text-gray-800 mb-4">Rendez-vous</h1>

                {/* Header avec titre et boutons */}
                <div className="mb-8">

                    {/* Barre de recherche */}
                    <div className="mb-4">
                        <div className="relative max-w-md">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                </svg>
                            </div>
                            <input
                                type="text"
                                placeholder="Rechercher par nom, téléphone ou email..."
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                onChange={(e) => setSearchTerm(e.target.value)}
                                value={searchTerm}
                            />
                        </div>
                    </div>


                    {/* Filtres de date */}
                    <div className="mb-4 w-full">
                        {/* Dropdown pour petits écrans */}
                        <div className="sm:hidden mb-2">
                            <select
                                value={dateFilter}
                                onChange={(e) => {
                                    const key = e.target.value;
                                    setDateFilter(key);
                                    if (key !== "custom") fetchRendezVous(key);
                                }}
                                className="w-full py-2 px-4 rounded-md border border-gray-300 text-sm bg-gray-100 text-gray-700"
                            >
                                {["today", "week", "month", "custom", "all"].map((key) => (
                                    <option key={key} value={key}>
                                        {{
                                            today: "Aujourd'hui",
                                            week: "Cette semaine",
                                            month: "Ce mois",
                                            custom: "Période",
                                            all: "Toutes dates",
                                        }[key]}
                                    </option>
                                ))}
                            </select>
                        </div>

                        {/* Boutons visibles uniquement sur les grands écrans */}
                        <div className="hidden sm:flex flex-wrap gap-2 justify-start sm:justify-between">
                            {["today", "week", "month", "custom", "all"].map((key) => (
                                <button
                                    key={key}
                                    onClick={() => {
                                        setDateFilter(key);
                                        if (key !== "custom") fetchRendezVous(key);
                                    }}
                                    className={`w-full sm:w-auto flex-1 py-2 px-4 rounded-md text-sm font-medium text-center ${dateFilter === key
                                        ? 'bg-gray-800 text-white'
                                        : 'bg-gray-100 text-gray-700'
                                        }`}
                                >
                                    {{
                                        today: "Aujourd'hui",
                                        week: "Cette semaine",
                                        month: "Ce mois",
                                        custom: "Période",
                                        all: "Toutes dates",
                                    }[key]}
                                </button>
                            ))}
                        </div>

                        {/* Période personnalisée */}
                        {dateFilter === "custom" && (
                            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Date de début</label>
                                    <input
                                        type="date"
                                        value={customStartDate}
                                        onChange={(e) => setCustomStartDate(e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Date de fin</label>
                                    <input
                                        type="date"
                                        value={customEndDate}
                                        onChange={(e) => setCustomEndDate(e.target.value)}
                                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                    />
                                </div>
                                <button
                                    onClick={() => fetchRendezVous("custom", customStartDate, customEndDate)}
                                    className="col-span-2 bg-gray-800 text-white py-2 px-4 rounded-md hover:bg-gray-600"
                                    disabled={!customStartDate || !customEndDate}
                                >
                                    Appliquer la période
                                </button>
                            </div>
                        )}
                    </div>


                    {/* Filtres améliorés - version full width */}
                    <div className="w-full">
                        <div className="flex flex-wrap gap-2 justify-start sm:justify-between">
                            <button
                                onClick={() => setFilter("all")}
                                className={`w-full sm:w-auto flex-1 py-2 px-4 rounded-md text-sm font-medium flex items-center justify-center ${filter === "all"
                                    ? 'bg-gray-800 text-white shadow-md'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                <span>Tous ({rendezVous.length})</span>
                            </button>
                            <button
                                onClick={() => setFilter("confirmed")}
                                className={`w-full sm:w-auto flex-1 py-2 px-4 rounded-md text-sm font-medium flex items-center justify-center ${filter === "confirmed"
                                    ? 'bg-green-600 text-white shadow-md'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                <span>Confirmés ({rendezVous.filter(r => r.confirmation === true).length})</span>
                            </button>
                            <button
                                onClick={() => setFilter("pending")}
                                className={`w-full sm:w-auto flex-1 py-2 px-4 rounded-md text-sm font-medium flex items-center justify-center ${filter === "pending"
                                    ? 'bg-yellow-500 text-white shadow-md'
                                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                    }`}
                            >
                                <span>En attente ({rendezVous.filter(r => r.confirmation === false).length})</span>
                            </button>
                        </div>
                    </div>



                </div>
                {/* Tableau des rendez-vous */}
                <div className="overflow-x-auto rounded-lg border border-gray-200">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Heure</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredRendezVous.length > 0 ? (

                                filteredRendezVous.map((rdv) => (
                                    <tr key={rdv._id} className="hover:bg-gray-50 transition-colors group">
                                        {/* Cellule Nom avec description en preview */}
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="relative">
                                                <button
                                                    className="text-sm font-medium text-gray-900 hover:text-blue-600 focus:outline-none text-left"
                                                    onClick={() => setConfirmingId(confirmingId === rdv._id ? null : rdv._id)}
                                                    onMouseEnter={(e) => {
                                                        // Affiche une preview au survol
                                                        const preview = e.currentTarget.nextElementSibling;
                                                        preview?.classList.remove('hidden');
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        const preview = e.currentTarget.nextElementSibling;
                                                        preview?.classList.add('hidden');
                                                    }}
                                                >
                                                    {rdv.clientFullName}
                                                    {rdv.description && (
                                                        <span className="ml-2 text-blue-500 text-xs">ℹ️</span>
                                                    )}
                                                </button>

                                                {/* Preview flottante */}
                                                <div className="hidden absolute z-50 left-0 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg p-3 text-sm">
                                                    <div className="font-semibold mb-1">Description :</div>
                                                    <div className="whitespace-pre-line text-gray-700 max-h-40 overflow-y-auto">
                                                        {rdv.description || "Aucune description"}
                                                    </div>
                                                    <button
                                                        className="mt-2 text-blue-600 text-xs hover:underline"
                                                        onClick={() => setConfirmingId(rdv._id)}
                                                    >
                                                        Voir tous les détails →
                                                    </button>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{rdv.clientPhoneNumber}</div>
                                            <div className="text-sm text-gray-500">{rdv.clientEmail}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {editingId === rdv._id ? (
                                                <input
                                                    type="date"
                                                    name="date"
                                                    value={editForm.date}
                                                    onChange={handleEditChange}
                                                    className="border rounded p-1 text-sm w-full" />
                                            ) : (
                                                <div className="text-sm text-gray-900">{formatDate(rdv.date)}</div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {editingId === rdv._id ? (
                                                <input
                                                    type="time"
                                                    name="heure"
                                                    value={editForm.heure}
                                                    onChange={handleEditChange}
                                                    className="border rounded p-1 text-sm w-full" />
                                            ) : (
                                                <div className="text-sm text-gray-900">{rdv.heure}</div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900 capitalize">{rdv.type}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">

                                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${rdv.confirmation === true
                                                ? "bg-green-100 text-green-800"
                                                : "bg-yellow-100 text-yellow-800"}`}>
                                                {rdv.confirmation === true ? "Confirmé" : "En attente"}
                                            </span>

                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                            {editingId === rdv._id ? (
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={saveEdit}
                                                        className="text-green-600 hover:text-green-800"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        onClick={() => setEditingId(null)}
                                                        className="text-gray-600 hover:text-gray-800"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            ) : (
                                                <div className="flex space-x-2">
                                                    <button
                                                        onClick={() => startEditing(rdv)}
                                                        className="text-blue-600 hover:text-blue-800"
                                                        title="Modifier"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                            <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        onClick={() => deleteRendezVous(rdv._id)}
                                                        className="text-red-600 hover:text-red-800"
                                                        title="Supprimer"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                                            <path fillRule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clipRule="evenodd" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                                        Aucun rendez-vous trouvé
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Modal des détails du rendez-vous */}
                {confirmingId && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] flex flex-col">
                            {/* En-tête avec le statut */}
                            <div className="flex items-center justify-between mb-4">
                                <h3 className="text-lg font-medium text-gray-900">Détails du rendez-vous</h3>
                                <span className={`px-2 py-1 text-xs font-medium rounded-full ${rendezVous.find(rdv => rdv._id === confirmingId)?.confirmation === true
                                    ? "bg-green-100 text-green-800"
                                    : "bg-yellow-100 text-yellow-800"}`}>
                                    {rendezVous.find(rdv => rdv._id === confirmingId)?.confirmation === true ? "Confirmé" : "En attente"}
                                </span>
                            </div>

                            {/* Contenu scrollable */}
                            <div className="flex-1 overflow-y-auto pr-2 mb-4">
                                {/* Détails du client */}
                                <div className="mb-4">
                                    <h4 className="font-medium text-gray-700">Client</h4>
                                    <p className="text-gray-900">{rendezVous.find(rdv => rdv._id === confirmingId)?.clientFullName}</p>
                                    <p className="text-sm text-gray-600">{rendezVous.find(rdv => rdv._id === confirmingId)?.clientPhoneNumber}</p>
                                    <p className="text-sm text-gray-600">{rendezVous.find(rdv => rdv._id === confirmingId)?.clientEmail}</p>
                                </div>

                                {/* Détails du RDV */}
                                <div className="grid grid-cols-2 gap-4 mb-6">
                                    <div>
                                        <h4 className="font-medium text-gray-700">Date</h4>
                                        <p className="text-gray-900">{formatDate(rendezVous.find(rdv => rdv._id === confirmingId)?.date)}</p>
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-gray-700">Heure</h4>
                                        <p className="text-gray-900">{rendezVous.find(rdv => rdv._id === confirmingId)?.heure}</p>
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-gray-700">Type</h4>
                                        <p className="text-gray-900 capitalize">{rendezVous.find(rdv => rdv._id === confirmingId)?.type}</p>
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-gray-700">Langue</h4>
                                        <p className="text-gray-900 capitalize">{rendezVous.find(rdv => rdv._id === confirmingId)?.preferredLanguage}</p>
                                    </div>
                                    <div>
                                        <h4 className="font-medium text-gray-700">Méthode de contact</h4>
                                        <p className="text-gray-900 capitalize">{rendezVous.find(rdv => rdv._id === confirmingId)?.contactMethod}</p>
                                    </div>
                                    {rendezVous.find(rdv => rdv._id === confirmingId)?.confirmation === true && (
                                        <div>
                                            <h4 className="font-medium text-gray-700">Confirmé par</h4>
                                            <p className="text-gray-900">{rendezVous.find(rdv => rdv._id === confirmingId)?.confirmedBy || "Système"}</p>
                                        </div>
                                    )}
                                </div>

                                {/* Description avec scroll si nécessaire */}
                                <div className="mb-4">
                                    <h4 className="font-medium text-gray-700 mb-2">Description</h4>
                                    <div className="max-h-40 overflow-y-auto p-2 bg-gray-50 rounded border border-gray-200">
                                        <p className="text-gray-900 whitespace-pre-line">
                                            {rendezVous.find(rdv => rdv._id === confirmingId)?.description || "Aucune description fournie"}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Actions (toujours visibles en bas) */}
                            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                                <button
                                    onClick={() => setConfirmingId(null)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                >
                                    Fermer
                                </button>

                                {rendezVous.find(rdv => rdv._id === confirmingId)?.confirmation !== true && (
                                    <button
                                        onClick={() => {
                                            confirmRendezVous(confirmingId);
                                            setConfirmingId(null);
                                        }}
                                        className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                                    >
                                        Confirmer le RDV
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal de création de rendez-vous */}
                {isCreateModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                        <div className="bg-white rounded-lg p-6 max-w-md w-full max-h-[90vh] flex flex-col">
                            <h3 className="text-lg font-medium text-gray-900 mb-4">Créer un nouveau rendez-vous</h3>

                            <div className="flex-1 overflow-y-auto pr-2 mb-4 space-y-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Nom complet</label>
                                    <input
                                        type="text"
                                        name="clientFullName"
                                        value={newRendezVous.clientFullName}
                                        onChange={handleCreateChange}
                                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Téléphone</label>
                                    <input
                                        type="tel"
                                        name="clientPhoneNumber"
                                        value={newRendezVous.clientPhoneNumber}
                                        onChange={handleCreateChange}
                                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Email</label>
                                    <input
                                        type="email"
                                        name="clientEmail"
                                        value={newRendezVous.clientEmail}
                                        onChange={handleCreateChange}
                                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Date</label>
                                        <input
                                            type="date"
                                            name="date"
                                            value={newRendezVous.date}
                                            onChange={handleCreateChange}
                                            className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Heure</label>
                                        <input
                                            type="time"
                                            name="heure"
                                            value={newRendezVous.heure}
                                            onChange={handleCreateChange}
                                            className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Type</label>
                                    <select
                                        name="type"
                                        value={newRendezVous.type}
                                        onChange={handleCreateChange}
                                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                    >
                                        <option value="Mécanique">Mécanique</option>
                                        <option value="Carrosserie">Carrosserie</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Langue préférée</label>
                                    <select
                                        name="preferredLanguage"
                                        value={newRendezVous.preferredLanguage}
                                        onChange={handleCreateChange}
                                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                    >
                                        <option value="fr">Français</option>
                                        <option value="en">English</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Méthode de contact</label>
                                    <select
                                        name="contactMethod"
                                        value={newRendezVous.contactMethod}
                                        onChange={handleCreateChange}
                                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                    >
                                        <option value="email">Email</option>
                                        <option value="phone">Phone</option>
                                        <option value="both">Both</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700">Description</label>
                                    <textarea
                                        name="description"
                                        value={newRendezVous.description}
                                        onChange={handleCreateChange}
                                        rows={3}
                                        className="mt-1 block w-full border border-gray-300 rounded-md p-2"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                                <button
                                    onClick={() => setIsCreateModalOpen(false)}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                                >
                                    Annuler
                                </button>
                                <button
                                    onClick={handleCreateRendezVous}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    Créer le RDV
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
            <style>{tooltipStyles}</style>

            {/* Bouton flottant pour ajouter un RDV */}
            <button
                onClick={() => setIsCreateModalOpen(true)}
                className="fixed bottom-8 right-8 bg-gray-800 hover:bg-blue-700 text-white rounded-full p-4 shadow-lg transition-all duration-300 hover:scale-110"
                aria-label="Ajouter un rendez-vous"
            >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
            </button>
        </>

    );
};

export default GestionRendezVous;